import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { paymentKeys, accountKeys, transactionKeys, invoiceKeys } from '@/lib/query-keys';
import { paymentService } from '@/services/payment.service';
import { usePaymentFilterStore } from '@/stores/paymentFilterStore';

export function usePayments() {
  const queryClient = useQueryClient();
  const filters = usePaymentFilterStore();

  const paymentsQuery = useQuery({
    queryKey: paymentKeys.list(filters.current),
    queryFn: () => paymentService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const invalidateRelated = () => {
    queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    queryClient.invalidateQueries({ queryKey: accountKeys.all });
    queryClient.invalidateQueries({ queryKey: transactionKeys.all });
    queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
  };

  const createPayment = useMutation({
    mutationFn: (data: Parameters<typeof paymentService.create>[0]) => paymentService.create(data),
    onSuccess: () => { invalidateRelated(); toast.success('Payment recorded'); },
    onError: () => toast.error('Failed to record payment'),
  });

  const updatePayment = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof paymentService.update>[1] }) => paymentService.update(id, data),
    onSuccess: () => { invalidateRelated(); toast.success('Payment updated'); },
    onError: () => toast.error('Failed to update payment'),
  });

  const deletePayment = useMutation({
    mutationFn: (id: string) => paymentService.delete(id),
    onSuccess: () => { invalidateRelated(); toast.success('Payment deleted'); },
    onError: () => toast.error('Failed to delete payment'),
  });

  return {
    payments: paymentsQuery.data?.data ?? [],
    meta: paymentsQuery.data?.meta,
    isLoading: paymentsQuery.isLoading,
    filters,
    createPayment, updatePayment, deletePayment,
  };
}
